import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { DecodedToken } from "./types/order";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const setAuthToken = (token: string) => {
  if (typeof window !== 'undefined')
    localStorage.setItem('auth_token', token);
};

export const getAuthToken = (): string | null => {
  if (typeof window === 'undefined')
    return null;
  return localStorage.getItem('auth_token');
};

export const removeAuthToken = () => {
  if (typeof window !== 'undefined')
    localStorage.removeItem('auth_token');
};


export const getImageURL = (imageSrc: string | null | undefined) => {
  if(!imageSrc) return null;
  if(imageSrc.startsWith('http://') || imageSrc.startsWith('https://')) {
    // Any absolute URL whose path starts with /images/ is a product image
    // served by the backend. Strip the domain and return a relative path so
    // the browser resolves it against the current host, which routes through
    // the AFD /images/ proxy (production) or Vite dev proxy (development).
    // This works regardless of which hostname the API returns — custom domain,
    // azurewebsites.net, or any other origin.
    try {
      const url = new URL(imageSrc);
      if (url.pathname.startsWith('/images/')) {
        return url.pathname + url.search;
      }
    } catch {
      // Invalid URL — fall through and return as-is
    }
    return imageSrc;
  }
  // Relative paths starting with /images/ are already proxied by AFD or
  // Vite dev server, so keep them as-is — the browser resolves them against
  // the current host (the frontend domain).
  if (imageSrc.startsWith('/images/')) {
    return imageSrc;
  }
  // Other relative paths (e.g. /api/...) need the backend domain prefix
  // because they are not proxied through the frontend domain.
  const path = imageSrc.startsWith('/') ? imageSrc : `/${imageSrc}`;
  return `https://${import.meta.env.VITE_BACKEND_CUSTOM_DOMAIN_NAME}${path}`;
};

export const getHTMLText = (html: string) => {
  if (html === 'No description available.' || html === null) return html;
  if (typeof window === 'undefined') {
    // Server-side fallback: strip HTML tags with regex
    return html.replace(/<[^>]*>/g, '').trim() || html;
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() || html;
};

export const ORDER_STATUS = {
  PENDING: { id: 10, label: 'Pending' },
  PROCESSING: { id: 20, label: 'Processing' },
  COMPLETE: { id: 30, label: 'Complete' },
  CANCELLED: { id: 40, label: 'Cancelled' }
} as const;

export const DEFAULT_FALLBACK_IMAGE = '/dan.webp';

export const formatUSD = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

export const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US').format(value);

export const decodeToken = (token: string): DecodedToken | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10)
  if (digits.length === 0) return ''
  if (digits.length <= 3) return `(${digits}`
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
};

export const validatePhone = (phone: string): boolean => {
  const digits = phone.replace(/\D/g, '')
  return digits.length === 10
};

/**
 * Converts a PascalCase string to camelCase
 * @example pascalToCamelCase('ProductPrice') => 'productPrice'
 */
export const pascalToCamelCase = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toLowerCase() + str.slice(1);
};

/**
 * Converts a snake_case string to camelCase
 * @example snakeToCamelCase('product_price') => 'productPrice'
 */
export const snakeToCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Converts a camelCase string to snake_case
 * @example camelToSnakeCase('productPrice') => 'product_price'
 */
export const camelToSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

/**
 * Recursively converts all keys in an object from PascalCase to camelCase
 * Handles nested objects and arrays
 * @template T The expected output type with camelCase keys
 */
export const pascalToCamelCaseObject = <T>(obj: unknown): T => {
  if (obj === null || obj === undefined) return obj as T;
  if (Array.isArray(obj)) {
    return obj.map(item => pascalToCamelCaseObject(item)) as T;
  }
  if (typeof obj !== 'object') return obj as T;

  const result: Record<string, unknown> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const camelKey = pascalToCamelCase(key);
      result[camelKey] = pascalToCamelCaseObject((obj as Record<string, unknown>)[key]);
    }
  }
  return result as T;
};

/**
 * Recursively converts all keys in an object from snake_case to camelCase
 * Handles nested objects and arrays
 * @template T The expected output type with camelCase keys
 */
export const snakeToCamelCaseObject = <T>(obj: unknown): T => {
  if (obj === null || obj === undefined) return obj as T;
  if (Array.isArray(obj)) {
    return obj.map(item => snakeToCamelCaseObject(item)) as T;
  }
  if (typeof obj !== 'object') return obj as T;

  const result: Record<string, unknown> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const camelKey = snakeToCamelCase(key);
      result[camelKey] = snakeToCamelCaseObject((obj as Record<string, unknown>)[key]);
    }
  }
  return result as T;
};

/**
 * Recursively converts all keys in an object from camelCase to snake_case
 * Handles nested objects and arrays
 * @template T The expected output type with snake_case keys
 */
export const camelToSnakeCaseObject = <T>(obj: unknown): T => {
  if (obj === null || obj === undefined) return obj as T;
  if (Array.isArray(obj)) {
    return obj.map(item => camelToSnakeCaseObject(item)) as T;
  }
  if (typeof obj !== 'object') return obj as T;

  const result: Record<string, unknown> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const snakeKey = camelToSnakeCase(key);
      result[snakeKey] = camelToSnakeCaseObject((obj as Record<string, unknown>)[key]);
    }
  }
  return result as T;
};
